<template>
  <div>
    <CustomFilterListing
      v-if="isFilterActive"
      v-bind="{
        currentPage,
        facets: searchFacets,
        hasRefinements,
        hitsPerPage,
        indexName,
        isFilterSectionVisible,
        results,
        resultType,
        returnToTitle: entry.title,
        totalHits,
        totalPages,
      }"
      @back-click="closeFilterPage"
    />
    <div v-else>
      <ExercisesHeader :image="entry.image[0]">
        <template #boxDesktop>
          <h1 class="fluid-3xl leading-tightest">{{ entry.title }}</h1>
          <div
            v-if="entry.teaserText"
            class="my-4"
            :class="[
              entry.teaserText.length < 220
                ? 'fluid-base leading-relaxed font-light'
                : 'fluid-base leading-relaxed font-light line-clamp-6',
            ]"
            v-html="entry.teaserText"
          ></div>
          <button
            v-if="entry.filterLinkText"
            class="hover:underline exercise-header__button"
            @click="toggleFilterVisibility"
          >
            {{ entry.filterLinkText }}
            <InlineArrowIcon class="inline h-2" />
          </button>
        </template>
        <template #boxMobile>
          <h1 class="fluid-3xl leading-tightest">{{ entry.title }}</h1>
          <div class="my-4 fluid-base leading-relaxed font-light" v-html="entry.teaserText"></div>
          <button v-if="entry.filterLinkText" data-test-id="show-filter" @click="toggleFilterVisibility">
            {{ entry.filterLinkText }}
            <InlineArrowIcon class="inline h-2" />
          </button>
        </template>
      </ExercisesHeader>

      <ContentBlocks :entry="entry" />
    </div>
  </div>
</template>

<script setup async>
import { onBeforeUnmount, onMounted, watch, ref } from 'vue';
import { useAlgoliaSearch } from '@/composables/useAlgoliaSearch';
import ExercisesHeader from '@/components/exercises/ExercisesHeader';
import InlineArrowIcon from '@/components/icons/InlineArrowIcon';
import ContentBlocks from '@/components/common/ContentBlocks';
import CustomFilterListing from '@/components/exercises/CustomFilterListing';
import { Bus } from '@/utils/bus';
import { setupFilterPresets } from '@/utils/algolia';

const props = defineProps({
  entry: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const isFilterSectionVisible = ref(route.query.filter === 'true');

// Emit filter visibility state via Bus
watch(
  isFilterSectionVisible,
  (newValue) => {
    Bus.emit('exercisesFilterVisibilityChanged', newValue);
  },
  { immediate: true }
);

// Use the Algolia composable with the page profile and filterPresets
const {
  isFilterActive,
  searchFacets,
  resultType,
  indexName,
  hitsPerPage,
  toggleFilter,
  clearFilters,
  currentPage,
  totalPages,
  totalHits,
  results,
  hasRefinements,
} = await useAlgoliaSearch({
  pageProfile: props.entry?.pageProfile,
  filterPresets: setupFilterPresets(props.entry),
});

const toggleFilterVisibility = () => {
  if (isFilterActive.value) {
    // If filter is already active, just toggle the filter section visibility
    isFilterSectionVisible.value = !isFilterSectionVisible.value;
  } else {
    // If filter is not active, make it active and show the filter section
    isFilterSectionVisible.value = true;
    toggleFilter();
  }
};

const closeFilterPage = () => {
  // Clear filters and reset the URL
  clearFilters();
  navigateTo({
    query: null,
  });
};

onMounted(() => {
  Bus.on('exercisesFilterToggle', () => {
    toggleFilterVisibility();
  });
});

onBeforeUnmount(() => {
  Bus.off('exercisesFilterToggle');
});
</script>
